import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getProxiedCloudinaryUrl } from '../utils/cloudinaryProxy';
import MenuItem from './MenuItem';
import ItemDetailModal from './ItemDetailModal';
import OrderSummary from './OrderSummary';
import BluetoothPrinter from './BluetoothPrinter';
import './POS.css';

const POS = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [tables, setTables] = useState([]);
  const [loadingTables, setLoadingTables] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const { user, signOut } = useAuth();

  useEffect(() => {
    if (user) {
      fetchCategories();
      fetchMenuItems();
    }
  }, [user]);

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/menu-categories', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const userCategories = data.data.filter(cat => cat.USERNAME === user?.username);
          const sortedCategories = userCategories.sort((a, b) => a.DISPLAY_ORDER - b.DISPLAY_ORDER);
          setCategories(sortedCategories);
          if (sortedCategories.length > 0) {
            setActiveCategory(sortedCategories[0].CATEGORY_ID);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const response = await fetch('/api/menu-items', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const userItems = data.data.filter(item => item.USERNAME === user?.username);
          setMenuItems(userItems);
        }
      }
    } catch (error) {
      console.error('Error fetching menu items:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectCategory = (categoryId) => {
    setActiveCategory(categoryId);
  };

  // Enhanced filtering logic that combines category and search
  const filteredMenuItems = menuItems.filter(item => {
    // Category filter
    const categoryMatch = activeCategory ? item.CATEGORY_ID === activeCategory : true;
    
    // Search filter
    const searchMatch = searchTerm.trim() === '' || 
      (item.NAME && item.NAME.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.name && item.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.ITEM_CODE && item.ITEM_CODE.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.code && item.code.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return categoryMatch && searchMatch;
  });

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const openItemModal = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const closeItemModal = () => {
    setSelectedItem(null);
    setIsModalOpen(false);
  };

  const addToCart = (itemWithVariations) => {
    console.log('POS - Adding to cart:', itemWithVariations);
    
    setCart(prevCart => {
      const existingItemIndex = findItemInCart(itemWithVariations);

      if (existingItemIndex !== -1) {
        // Item with same variations exists, update quantity
        const updatedCart = [...prevCart];
        updatedCart[existingItemIndex].quantity += itemWithVariations.quantity;
        console.log('POS - Updated existing item:', updatedCart[existingItemIndex]);
        return updatedCart;
      } else {
        // Add new item to cart
        const newCartItem = {
          id: itemWithVariations.ITEM_ID,
          name: itemWithVariations.NAME,
          basePrice: parseFloat(itemWithVariations.BASE_PRICE) || 0,
          totalPrice: itemWithVariations.totalPrice || parseFloat(itemWithVariations.BASE_PRICE) || 0,
          price: itemWithVariations.totalPrice || parseFloat(itemWithVariations.BASE_PRICE) || 0, // For backward compatibility
          quantity: itemWithVariations.quantity,
          itemCode: itemWithVariations.ITEM_CODE || '',
          orderType: itemWithVariations.orderType,
          variations: itemWithVariations.selectedVariations || [],
          imageUrl: getProxiedCloudinaryUrl(itemWithVariations.IMAGE_URL || '../img/dish.png')
        };
        
        console.log('POS - Adding new cart item:', newCartItem);
        return [...prevCart, newCartItem];
      }
    });
  };

  const findItemInCart = (newItem) => {
    for (let i = 0; i < cart.length; i++) {
      const item = cart[i];

      // Check if same item ID
      if (item.id === newItem.ITEM_ID) {
        // If no variations for both, it's a match
        if (!newItem.selectedVariations?.length && !item.variations?.length) {
          return i;
        }

        // If variations length doesn't match, not a match
        if ((newItem.selectedVariations?.length || 0) !== (item.variations?.length || 0)) {
          continue;
        }

        // Check if all variations match
        const allVariationsMatch = newItem.selectedVariations?.every(variation => {
          return item.variations?.some(itemVariation =>
            itemVariation.id === variation.id &&
            itemVariation.name === variation.name
          );
        });

        if (allVariationsMatch) {
          return i;
        }
      }
    }

    return -1;
  };

  const removeFromCart = (itemId, itemVariations = null) => {
    setCart(prevCart => {
      if (itemVariations) {
        // Remove specific item with variations
        return prevCart.filter((item, index) => {
          if (item.id !== itemId) return true;
          
          // If variations don't match, keep the item
          if ((item.variations?.length || 0) !== (itemVariations?.length || 0)) {
            return true;
          }
          
          // Check if variations match exactly
          const variationsMatch = item.variations?.every(variation =>
            itemVariations?.some(itemVar =>
              itemVar.id === variation.id && itemVar.name === variation.name
            )
          );
          
          return !variationsMatch;
        });
      } else {
        // Remove by ID only (backward compatibility)
        return prevCart.filter(item => item.id !== itemId);
      }
    });
  };

  const updateQuantity = (itemId, quantity, itemVariations = null) => {
    if (quantity <= 0) {
      removeFromCart(itemId, itemVariations);
    } else {
      setCart(prevCart =>
        prevCart.map(item => {
          if (item.id !== itemId) return item;
          
          // If variations are provided, check if they match
          if (itemVariations) {
            if ((item.variations?.length || 0) !== (itemVariations?.length || 0)) {
              return item;
            }
            
            const variationsMatch = item.variations?.every(variation =>
              itemVariations?.some(itemVar =>
                itemVar.id === variation.id && itemVar.name === variation.name
              )
            );
            
            if (!variationsMatch) return item;
          }
          
          return { ...item, quantity };
        })
      );
    }
  };

  const clearCart = () => {
    setCart([]);
    setSelectedTable(null);
  };

  const handlePaymentSuccess = async (paymentResponse) => {
    try {
      // Check if we're in development mode
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

      if (isDevelopment) {
        // Simulate successful order saving in development
        console.log('Development mode: Simulating order save', { cart, paymentResponse });
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API delay
        clearCart();
        alert('Order placed successfully! (Development Mode)');
        return;
      }

      // Order is already created and payment is processed by the callback
      // Just clear the cart and show success message
      console.log('Payment successful:', paymentResponse);

      // Clear cart after successful payment
      clearCart();
      alert('Payment successful! Order has been processed.');

    } catch (error) {
      console.error('Error in payment success handler:', error);
      alert('Payment successful but there was an issue. Please contact support.');
    }
  };

  const getTotal = () => {
    return cart.reduce((total, item) => {
      const itemPrice = item.totalPrice || item.price || 0;
      return total + (itemPrice * item.quantity);
    }, 0);
  };

  const fetchClientProfile = async () => {
    try {
      // Get username from user object or fallback to localStorage
      const username = user?.username || localStorage.getItem('username');

      if (!username) {
        console.warn('No username available for client profile fetch');
        return 0;
      }

      console.log('fetchClientProfile - Fetching for username:', username);

      const response = await fetch(`/api/clients/by-username/${encodeURIComponent(username)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const tableCount = data?.data?.table || 0;
        console.log('fetchClientProfile - Table count:', tableCount);
        return tableCount;
      }

      console.warn('fetchClientProfile - Response not ok:', response.status);
      return 0;
    } catch (error) {
      console.error('Error fetching client profile:', error);
      return 0;
    }
  };

  const fetchPendingOrders = async () => {
    try {
      setLoadingTables(true);
      
      // Force a specific username for testing
      const username = 'vincent423'; // Hardcoded for debugging
      
      console.log('fetchPendingOrders - Using hardcoded username:', username);
      console.log('fetchPendingOrders - Current location:', window.location.href);
      console.log('fetchPendingOrders - Current origin:', window.location.origin);
      console.log('fetchPendingOrders - Current pathname:', window.location.pathname);
      
      // Get configured table count from client profile
      const configuredTableCount = await fetchClientProfile();
      console.log('fetchPendingOrders - Configured table count:', configuredTableCount);
      
      // If no tables configured, create a default set for testing
      const actualTableCount = configuredTableCount > 0 ? configuredTableCount : 10; // Default to 10 tables for testing
      console.log('fetchPendingOrders - Using table count:', actualTableCount);
      
      // Fetch all orders for the user - using same pattern as working tables.html
      const apiUrl = `../api/orders?username=${encodeURIComponent(username)}`;
      console.log('fetchPendingOrders - API URL:', apiUrl);
      console.log('fetchPendingOrders - Encoded username:', encodeURIComponent(username));
      
      console.log('fetchPendingOrders - About to make fetch request to:', apiUrl);
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
        
      console.log('fetchPendingOrders - Response status:', response.status);
      console.log('fetchPendingOrders - Response ok:', response.ok);

      let ordersData = [];
      if (response.ok) {
        const data = await response.json();
        console.log('fetchPendingOrders - Response data:', data);
        if (data.success) {
          ordersData = data.data || [];
        }
      } else {
        const errorText = await response.text();
        console.error('fetchPendingOrders - API Error:', response.status, errorText);
      }
        
      // Create table objects based on configured count
      const tableObjects = [];
      if (actualTableCount > 0) {
        for (let i = 1; i <= actualTableCount; i++) {
          const tableNum = i.toString();
          
          // Filter orders for this table with PENDING status only and matching username (exact same logic as tables.html)
          const tableOrders = ordersData.filter(order => {
            const orderStatus = order.status?.toUpperCase() || '';
            const orderTableNum = String(order.table_number);
            const thisTableNum = String(tableNum);
            
            // Only include PENDING orders for this specific table
            return orderTableNum === thisTableNum && 
                   ['PENDING'].includes(orderStatus) &&
                   order.username === username;
          });

          const table = {
            TABLE_ID: `local-${i}`,
            TABLE_NUMBER: tableNum,
            USERNAME: username,
            STATUS: tableOrders.length > 0 ? 'HAS_ORDERS' : 'AVAILABLE',
            orders: tableOrders
          };
          
          tableObjects.push(table);
        }
      }

      console.log('fetchPendingOrders - Created table objects:', tableObjects.length);
      console.log('fetchPendingOrders - Table objects sample:', tableObjects.slice(0, 3));

      // Sort tables by number
      tableObjects.sort((a, b) => {
        const aNum = parseInt(a.TABLE_NUMBER);
        const bNum = parseInt(b.TABLE_NUMBER);
        return aNum - bNum;
      });

      console.log('fetchPendingOrders - Setting tables state');
      setTables(tableObjects);
      setPendingOrders(ordersData.filter(order => 
        order.status?.toUpperCase() === 'PENDING' && 
        order.username === username
      ));
      
    } catch (error) {
      console.error('Error fetching tables and orders:', error);
      setTables([]);
      setPendingOrders([]);
    } finally {
      setLoadingTables(false);
    }
  };

  const handleTableClick = (table) => {
    // If table has orders, load ALL pending orders into cart and combine them
    if (table.orders && table.orders.length > 0) {
      const allCartItems = [];
      const orderNumbers = [];

      // Process each order for this table
      table.orders.forEach(order => {
        if (order.order_items && order.order_items.length > 0) {
          // Store order number for display
          if (order.order_number || order.id) {
            orderNumbers.push(order.order_number || order.id);
          }

          // Process each item in this order
          order.order_items.forEach(item => {
            // Handle variations from multiple possible sources (same as tables.html)
            let variationsArr = [];
            if (item.variations && Array.isArray(item.variations)) {
              variationsArr = variationsArr.concat(item.variations);
            }
            if (item.selected_variations && Array.isArray(item.selected_variations)) {
              variationsArr = variationsArr.concat(item.selected_variations);
            }
            if (item.order_item_variations && Array.isArray(item.order_item_variations)) {
              variationsArr = variationsArr.concat(item.order_item_variations);
            }

            // Remove duplicates by name
            const seenNames = new Set();
            variationsArr = variationsArr.filter(v => {
              const n = v.name || v.variation_name || v.variant_name || '';
              if (seenNames.has(n)) return false;
              seenNames.add(n);
              return true;
            });

            const cartItem = {
              id: item.item_id,
              name: item.name || item.item_name || item.menu_item_name || 'Item',
              basePrice: parseFloat(item.price || item.unit_price || 0),
              totalPrice: parseFloat(item.total_price || item.price || 0),
              price: parseFloat(item.total_price || item.price || 0),
              quantity: parseInt(item.quantity) || 1,
              itemCode: item.item_code || '',
              orderType: item.order_type || 'dine-in',
              variations: variationsArr,
              imageUrl: getProxiedCloudinaryUrl(item.image_url || '../img/dish.png'),
              special_instructions: item.special_instructions,
              orderNumber: order.order_number || order.id // Track which order this item came from
            };

            // Add each item separately without combining quantities
            allCartItems.push(cartItem);
          });
        }
      });

      // Clear current cart and add all combined order items
      setCart(allCartItems);

      // Set the selected table for display with order information
      const tableWithOrderInfo = {
        ...table,
        combinedOrderNumbers: orderNumbers,
        totalOrders: table.orders.length
      };
      setSelectedTable(tableWithOrderInfo);

      // Close the modal after loading the orders
      setIsTableModalOpen(false);

      // Show success message
      if (orderNumbers.length === 1) {
        console.log(`Loaded order #${orderNumbers[0]} for table ${table.TABLE_NUMBER}`);
      } else {
        console.log(`Loaded ${orderNumbers.length} orders (${orderNumbers.join(', ')}) for table ${table.TABLE_NUMBER}`);
      }
    }
  };

  const closeTableModal = () => {
    setIsTableModalOpen(false);
  };

  // Fetch pending orders when modal opens
  useEffect(() => {
    console.log('useEffect triggered - isTableModalOpen:', isTableModalOpen, 'user:', !!user);
    if (isTableModalOpen && user) {
      console.log('Calling fetchPendingOrders');
      fetchPendingOrders();
    }
  }, [isTableModalOpen, user]);

  if (loading) {
    return <div className="loading">Loading menu...</div>;
  }

  return (
    <div className="pos-container">
      <header className="pos-header">
        <div className="header-left">
          <h1>POS System</h1>
          <button 
            onClick={() => setIsTableModalOpen(true)} 
            className="table-btn"
            title="View Tables with Pending Orders"
          >
            <i className="fas fa-utensils"></i>
            Tables
          </button>
        </div>
        <div className="user-info">
          <span>Welcome, {user?.username}</span>
          <button onClick={signOut} className="logout-btn">Logout</button>
        </div>
      </header>

      <div className="pos-main">
        {/* Categories Sidebar */}
        <div className="categories-sidebar">
          <div className="categories-container">
            {categories.map(category => (
              <div
                key={category.CATEGORY_ID}
                className={`category-tab ${activeCategory === category.CATEGORY_ID ? 'active' : ''}`}
                onClick={() => selectCategory(category.CATEGORY_ID)}
              >
                <div
                  className="category-image"
                  style={{
                    backgroundImage: category.GROUP_IMGURL ? `url('${getProxiedCloudinaryUrl(category.GROUP_IMGURL)}')` : 'none',
                    backgroundColor: !category.GROUP_IMGURL ? '#0093d0' : 'transparent'
                  }}
                ></div>
                <div className="category-details">
                  <div className="category-name">{category.NAME}</div>
                  <div className="category-desc">{category.DESCRIPTION || 'Food items in this category'}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Menu Items */}
        <div className="menu-section">
          <div className="menu-header">
            <h2>Menu Items</h2>
            <div className="search-container">
              <input
                type="text"
                placeholder="Search by name or code..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="menu-search-input"
                autoComplete="off"
                inputMode="text"
              />
              {searchTerm && (
                <button 
                  onClick={clearSearch}
                  className="search-clear-btn"
                  type="button"
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          </div>
          <div className="menu-scroll-container">
            <div className="menu-items-container">
              {filteredMenuItems.length === 0 ? (
                <div className="menu-empty">
                  <div className="empty-icon">
                    <i className="fas fa-utensils"></i>
                  </div>
                  <h4>No Items Found</h4>
                  <p>No items available in this category</p>
                </div>
              ) : (
                filteredMenuItems.map(item => (
                  <MenuItem
                    key={item.ITEM_ID}
                    item={item}
                    onOpenModal={openItemModal}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <div className="order-section">
          <div className="order-scroll-container">
            {selectedTable && (
              <div className="selected-table-indicator">
                <div className="table-indicator-header">
                  <h3>Table {selectedTable.TABLE_NUMBER}</h3>
                  <span className="table-status">
                    {selectedTable.totalOrders === 1 ? 'Order in Progress' : `${selectedTable.totalOrders} Orders Combined`}
                  </span>
                </div>
                <div className="order-info">
                  {selectedTable.totalOrders === 1 ? (
                    <span>Order #{selectedTable.combinedOrderNumbers?.[0] || 'N/A'}</span>
                  ) : (
                    <div className="multiple-orders-info">
                      <span>Combined Orders: {selectedTable.combinedOrderNumbers?.join(', ') || 'Multiple orders'}</span>
                      <span className="orders-count">({selectedTable.totalOrders} total orders)</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            <OrderSummary
              cart={cart}
              total={getTotal()}
              onUpdateQuantity={updateQuantity}
              onRemoveItem={removeFromCart}
              onClearCart={clearCart}
              onPaymentSuccess={handlePaymentSuccess}
              selectedTable={selectedTable}
            />
            <BluetoothPrinter cart={cart} total={getTotal()} />
          </div>
        </div>
      </div>

      <ItemDetailModal
        item={selectedItem}
        isOpen={isModalOpen}
        onClose={closeItemModal}
        onAddToCart={addToCart}
      />

      {/* Tables Modal */}
      {isTableModalOpen && (
        <div className="modal-overlay" onClick={closeTableModal}>
          <div className="modal-content tables-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>Tables with Pending Orders</h4>
              <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
                <button 
                  onClick={() => {
                    console.log('Manual fetch triggered');
                    fetchPendingOrders();
                  }}
                  style={{
                    padding: '0.25rem 0.5rem',
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  Refresh
                </button>
                <button onClick={closeTableModal} className="modal-close-btn">
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
            <div className="modal-body">
              {console.log('Modal rendering - loadingTables:', loadingTables, 'tables length:', tables.length)}
              {loadingTables ? (
                <div className="loading-tables">
                  <i className="fas fa-spinner fa-spin"></i>
                  <p>Loading tables...</p>
                </div>
              ) : tables.length === 0 ? (
                <div className="no-tables">
                  <i className="fas fa-utensils"></i>
                  <p>No tables configured. Please contact support to configure tables.</p>
                </div>
              ) : (
                <div className="tables-grid">
                  {console.log('Rendering tables:', tables)}
                  {tables.map(table => {
                    const formattedNumber = table.TABLE_NUMBER.toString().padStart(3, '0');
                    const hasOrders = table.orders && table.orders.length > 0;
                    
                    return (
                      <div 
                        key={table.TABLE_ID}
                        className={`table-card ${hasOrders ? 'has-orders' : ''}`}
                        onClick={() => handleTableClick(table)}
                      >
                        <div className="table-number-display">
                          {formattedNumber}
                        </div>
                        {hasOrders && (
                          <div className="order-badge">
                            {table.orders.length}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POS;